import { ApiService } from './ApiService';

const API_PREFIX = '/email';

export interface EmailTemplate {
	id: number;
	name: string;
	description?: string;
	template: string;
	variables?: Array<{
		name: string;
		type: string;
		required: boolean;
		defaultValue?: unknown;
		description?: string;
	}>;
	isActive: boolean;
	metadata?: Record<string, unknown>;
	createdDate: string;
	modifiedDate: string;
	variableCount?: number;
	requiredVariableCount?: number;
}

export interface CreateEmailTemplateRequest {
	name: string;
	description?: string;
	template?: string;
	variables?: EmailTemplate['variables'];
	isActive?: boolean;
	metadata?: Record<string, unknown>;
}

export interface UpdateEmailTemplateRequest
	extends Partial<CreateEmailTemplateRequest> {}

export interface EmailTemplateListResponse {
	success: boolean;
	data: Array<EmailTemplate>;
	pagination: {
		total: number;
		page: number;
		limit: number;
		totalPages: number;
	};
}

export interface EmailRecordModel {
	id: string;
	sourceType: 'provider_sync' | 'system_send' | 'user_send';
	sourceProvider?: string;
	messageId: string;
	subject: string;
	date?: Date;
	type: string;
	preview?: string;
	content?: string;
	sentAt?: Date;
	failedAt?: Date;
	errorMessage?: string;
	sendStatus?: 'pending' | 'sent' | 'failed' | 'bounced' | 'complained';
	fromEmail?: string;
	fromName?: string;
	hasAttachments: boolean;
	bodyType: 'text' | 'html';
	emailTemplateId?: number;
	automationStepExecutionId?: number;
	trackingEnabled: boolean;
	metadata?: Record<string, unknown>;
	recipientsDisplay?: string;
	eventsCount?: number;
	recipients?: Array<{
		id: string;
		type: 'to' | 'cc' | 'bcc';
		email: string;
		name?: string;
	}>;
	attachments?: Array<{
		id: string;
		name: string;
		mimeType: string;
		size?: number;
	}>;
	events?: Array<EmailEventModel>;
	emailTemplate?: {
		id: number;
		name: string;
	};
	automationStepExecution?: {
		id: number;
		executionId: number;
	};
}

export interface EmailEventModel {
	id: string;
	eventType:
		| 'sent'
		| 'opened'
		| 'clicked'
		| 'bounced'
		| 'complained'
		| 'delivered'
		| 'rejected'
		| 'delayed';
	eventTimestamp: Date;
	ipAddress?: string;
	userAgent?: string;
	url?: string;
	bounceType?: string;
	bounceSubtype?: string;
	rawEventData?: Record<string, unknown>;
}

export type SystemSentEmailsQueryParams = {
	page?: number;
	limit?: number;
	search?: string;
	sortFields?: string;
	filters?: string;
};

export interface SystemSentEmailsResponse {
	success: boolean;
	data: Array<EmailRecordModel>;
	pagination?: {
		total: number;
		totalPages: number;
		page: number;
		limit: number;
	};
	error?: string;
}

export class EmailApiService {
	// ===================
	// TEMPLATE OPERATIONS
	// ===================

	async getEmailTemplates(params?: {
		page?: number;
		limit?: number;
		search?: string;
		sortFields?: string;
		filters?: string;
		cacheKey?: string;
		cacheTTL?: number;
	}): Promise<EmailTemplateListResponse> {
		const response = await ApiService.apiGet<
			EmailTemplateListResponse & {
				templates?: Array<EmailTemplate>;
				total?: number;
			}
		>(`${API_PREFIX}/templates`, params);

		// Handle both wrapped and direct response formats
		if (response?.templates) {
			// Add computed fields for variable counts
			const templatesWithCounts = response.templates.map(
				(template: EmailTemplate) => ({
					...template,
					variableCount: template.variables?.length ?? 0,
					requiredVariableCount:
						template.variables?.filter((v) => v.required).length ?? 0,
				}),
			);

			return {
				success: true,
				data: templatesWithCounts,
				pagination: {
					total: response.total ?? templatesWithCounts.length,
					page: params?.page ?? 1,
					limit: params?.limit ?? 20,
					totalPages: Math.ceil(
						(response.total ?? templatesWithCounts.length) /
							(params?.limit ?? 20),
					),
				},
			};
		}

		// If response is already in the expected format
		if (response?.data && response?.pagination) {
			return response;
		}

		// Fallback for direct array response
		const templates = Array.isArray(response) ? response : [];
		const templatesWithCounts = templates.map((template: EmailTemplate) => ({
			...template,
			variableCount: template.variables?.length ?? 0,
			requiredVariableCount:
				template.variables?.filter((v) => v.required).length ?? 0,
		}));

		return {
			success: true,
			data: templatesWithCounts,
			pagination: {
				total: templatesWithCounts.length,
				page: 1,
				limit: templatesWithCounts.length,
				totalPages: 1,
			},
		};
	}

	async getEmailTemplate(templateId: number): Promise<EmailTemplate> {
		const response = await ApiService.apiGet<
			EmailTemplate & { data?: EmailTemplate }
		>(`${API_PREFIX}/templates/${templateId}`);
		// Handle both wrapped and direct responses
		const template = response?.data !== undefined ? response.data : response;

		return {
			...template,
			variableCount: template.variables?.length ?? 0,
			requiredVariableCount:
				template.variables?.filter((v: { required: boolean }) => v.required)
					.length ?? 0,
		};
	}

	async createEmailTemplate(
		data: CreateEmailTemplateRequest,
	): Promise<EmailTemplate> {
		const response = await ApiService.apiPost<
			EmailTemplate & { data?: EmailTemplate }
		>(`${API_PREFIX}/templates`, data);
		// Handle both wrapped and direct responses
		return response?.data !== undefined ? response.data : response;
	}

	async updateEmailTemplate(
		templateId: number,
		data: UpdateEmailTemplateRequest,
	): Promise<EmailTemplate> {
		const response = await ApiService.apiPut<
			EmailTemplate & { data?: EmailTemplate }
		>(`${API_PREFIX}/templates/${templateId}`, data);
		// Handle both wrapped and direct responses
		return response?.data !== undefined ? response.data : response;
	}

	async deleteEmailTemplate(templateId: number): Promise<void> {
		await ApiService.apiDelete(`${API_PREFIX}/templates/${templateId}`);
	}

	async testEmailTemplate(template: string): Promise<{ rendered: string }> {
		const response = await ApiService.apiPost<{
			rendered: string;
			data?: { rendered: string };
		}>(`${API_PREFIX}/templates/test`, { template });
		// Handle both wrapped and direct responses
		return response?.data !== undefined ? response.data : response;
	}

	// ============================
	// SYSTEM SENT EMAIL OPERATIONS
	// ============================

	async getSystemSentEmails(
		params: SystemSentEmailsQueryParams,
	): Promise<SystemSentEmailsResponse> {
		try {
			const response = await ApiService.apiGet<SystemSentEmailsResponse>(
				`${API_PREFIX}/system-sent`,
				params,
			);

			if (response?.data && response?.pagination) {
				return response;
			}

			// Fallback for direct array response
			const emails = Array.isArray(response) ? response : [];

			return {
				success: true,
				data: emails,
				pagination: {
					total: emails.length,
					page: 1,
					limit: emails.length,
					totalPages: 1,
				},
			};
		} catch (error) {
			console.error('Error fetching system sent emails:', error);

			return {
				success: false,
				data: [],
				error:
					error instanceof Error
						? error.message
						: 'Failed to fetch system sent emails',
			};
		}
	}

	async getSystemSentEmailById(id: string): Promise<{
		success: boolean;
		data?: EmailRecordModel;
		error?: string;
	}> {
		try {
			const response = await ApiService.apiGet<
				EmailRecordModel & { data?: EmailRecordModel }
			>(`${API_PREFIX}/system-sent/${id}`);

			return {
				success: true,
				data: response?.data !== undefined ? response.data : response,
			};
		} catch (error) {
			console.error('Error fetching email details:', error);

			return {
				success: false,
				error:
					error instanceof Error
						? error.message
						: 'Failed to fetch email details',
			};
		}
	}

	async getEmailEvents(emailId: string): Promise<{
		success: boolean;
		data?: Array<EmailEventModel>;
		error?: string;
	}> {
		try {
			const response = await ApiService.apiGet<
				Array<EmailEventModel> & { data?: Array<EmailEventModel> }
			>(`${API_PREFIX}/events/${emailId}`);

			return {
				success: true,
				data: response?.data !== undefined ? response.data : response,
			};
		} catch (error) {
			console.error('Error fetching email events:', error);

			return {
				success: false,
				error:
					error instanceof Error
						? error.message
						: 'Failed to fetch email events',
			};
		}
	}

	async getEmailAttachment(
		emailId: string,
		attachmentId: string,
	): Promise<{
		success: boolean;
		url?: string;
		error?: string;
	}> {
		try {
			const response = await ApiService.apiGet<string & { url?: string }>(
				`${API_PREFIX}/system-sent/${emailId}/attachments/${attachmentId}`,
			);

			return {
				success: true,
				url: response?.url !== undefined ? response.url : response,
			};
		} catch (error) {
			console.error('Error fetching attachment URL:', error);

			return {
				success: false,
				error:
					error instanceof Error ? error.message : 'Failed to fetch attachment',
			};
		}
	}
}
